/**
 * Water Samples API service
 * Maneja el formulario "Acta de muestras de agua" (Legionella, etc.)
 */

import { httpClient } from '@/utils';
import { config } from '@/config';

/**
 * Selectores soportados por el backend (ver Backend/apps/water_samples/models.py)
 */
export type WaterSampleContainerType =
    | 'plastico'
    | 'vidrio'
    | 'bote_esteril_neutralizante'
    | 'other';

export type WaterSampleContainerVolume = '1l' | '0_5l' | '0_10l' | 'other';

export type WaterSampleAnalysisType =
    | 'legionella'
    | 'aerobios_22c'
    | 'hierro'
    | 'other';

export type WaterSampleNature = 'agua' | 'biocapa' | 'other';

export type WaterSampleControlType = 'routine' | 'post_cleaning' | 'outbreak';

export type WaterSampleSystemType = 'acs' | 'af' | 'cl';

export type WaterSampleSamplingType = 'immersion' | 'pre_flush' | 'post_flush';

export interface WaterSampleLinePayload {
    id?: number;
    system_type: WaterSampleSystemType;
    sampling_type: WaterSampleSamplingType;
    fe_ppm?: string | number | null;
    cl_ppm?: string | number | null;
    temperature_c?: string | number | null;
    ph?: string | number | null;
    sample_time?: string | null;
    sampling_place?: string;
}

export interface WaterSampleLine extends WaterSampleLinePayload {
    id: number;
    ref: string;
}

export interface WaterSampleFormPayload {
    client: number;
    technician?: number | null;
    date: string;
    container_types: WaterSampleContainerType[];
    container_volume?: WaterSampleContainerVolume | '';
    container_other?: string | null;
    analysis_types: WaterSampleAnalysisType[];
    analysis_other?: string | null;
    sample_natures: WaterSampleNature[];
    sample_nature_other?: string | null;
    control_type: WaterSampleControlType;
    transportista?: string;
    observations?: string | null;
    samples: WaterSampleLinePayload[];
}

export interface WaterSampleForm extends WaterSampleFormPayload {
    id: number;
    samples: WaterSampleLine[];
    client_detail?: { id: number; name: string; address: string } | null;
    technician_detail?: { id: number; name: string } | null;
    created_at: string;
    updated_at: string;
}

const ENDPOINTS = {
    base: '/water-sample-forms/',
    byId: (id: number) => `/water-sample-forms/${id}/`,
    exportPdf: (id: number) => `/water-sample-forms/${id}/export-pdf/`,
} as const;

interface PaginatedResponse<T> {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
}

export const waterSamplesService = {
    async list(params?: { client?: number; pageSize?: number }): Promise<WaterSampleForm[]> {
        const search = new URLSearchParams();
        search.set('page_size', String(params?.pageSize ?? 100));
        if (params?.client !== undefined) {
            search.set('client', String(params.client));
        }
        const endpoint = `${ENDPOINTS.base}?${search.toString()}`;
        const res = await httpClient.get<PaginatedResponse<WaterSampleForm> | WaterSampleForm[]>(endpoint);
        if (Array.isArray(res)) return res;
        return res.results ?? [];
    },

    /**
     * Localiza un acta ya existente para una orden de trabajo.
     * El backend no tiene FK directa, así que se identifica por cliente
     * + presencia del nº de orden en las observaciones (formato generado
     * automáticamente por el modal al crear el acta).
     */
    async findByWorkOrder(params: {
        clientId: number;
        workOrderNumber: string;
    }): Promise<WaterSampleForm | null> {
        if (!params.clientId || !params.workOrderNumber) return null;
        try {
            const list = await this.list({ client: params.clientId });
            const needle = params.workOrderNumber.toLowerCase();
            return (
                list.find((form) =>
                    (form.observations ?? '').toLowerCase().includes(needle),
                ) ?? null
            );
        } catch {
            return null;
        }
    },

    async create(data: WaterSampleFormPayload): Promise<WaterSampleForm> {
        return httpClient.post<WaterSampleForm, WaterSampleFormPayload>(
            ENDPOINTS.base,
            data,
            {
                successTitle: 'Acta de muestras creada',
                successMessage: 'El acta de muestras de agua se guardó correctamente.',
                errorTitle: 'Error al guardar acta',
            },
        );
    },

    async getById(id: number): Promise<WaterSampleForm> {
        return httpClient.get<WaterSampleForm>(ENDPOINTS.byId(id));
    },

    async update(id: number, data: WaterSampleFormPayload): Promise<WaterSampleForm> {
        return httpClient.put<WaterSampleForm, WaterSampleFormPayload>(
            ENDPOINTS.byId(id),
            data,
            {
                successTitle: 'Acta actualizada',
                successMessage: 'Los cambios del acta se guardaron correctamente.',
                errorTitle: 'Error al guardar acta',
            },
        );
    },

    exportPdfUrl(id: number): string {
        return ENDPOINTS.exportPdf(id);
    },

    /**
     * Descarga el PDF del acta usando el endpoint export-pdf del backend.
     * Realiza la petición autenticada y dispara la descarga en el navegador.
     */
    async exportPdf(id: number): Promise<void> {
        const baseUrl = config.api.baseUrl.replace(/\/$/, '');
        const url = `${baseUrl}${ENDPOINTS.exportPdf(id)}`;
        const token = (() => {
            try {
                return localStorage.getItem('access_token');
            } catch {
                return null;
            }
        })();

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                Accept: '*/*',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });

        if (!response.ok) {
            throw new Error(`No se pudo exportar el acta (HTTP ${response.status}).`);
        }

        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `acta-muestras-agua-${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
    },
};

export default waterSamplesService;
